import { Carousel } from '@mantine/carousel'
import { Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconCaretLeftFilled, IconCaretRightFilled } from '@tabler/icons-react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import missingImage from '@/assets/webp_converted/imageOnload.webp'
import { useErrorMessages } from '@/constants/content'
import useSessionStorage from '@/hooks/useSessionStorage'
import { Entry, GET_STARTED_CONTENT_TYPE } from '@/plugins/contentful/api'
import client from '@/plugins/contentful/client'

import RootButton from './Buttons/RootButton'
import {
  GalleryElementsImagesByEntryId,
  pullGalleryImage,
  wrapData,
} from './Discover/DiscoverCarousel'
import ErrorMessage from './ErrorMessage'
import Loading from './Loading'

interface CTACardProps {
  image: string | undefined
  title: string
  description?: string
  button: {
    text?: string
    link: string
  }
}

export const CTACard: React.FC<CTACardProps> = ({ image, title, description = '', button }) => {
  const theme = useMantineTheme()
  const navigate = useNavigate()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  return (
    <Stack
      style={{ width: 292, cursor: 'pointer' }}
      onClick={() =>
        button.link.startsWith('/') ? navigate(button.link) : window.open(button.link, '_blank')
      }
    >
      <img
        src={image || missingImage}
        style={{ width: 292, height: 165, borderRadius: 12 }}
        alt="card"
      />
      <Title order={4}>{title}</Title>
      {description && <Text style={{ fontSize: isMobile ? 14 : 16 }}>{description}</Text>}
      {button.text && (
        <RootButton
          style1={false}
          onClick={() =>
            button.link.startsWith('/') ? navigate(button.link) : window.open(button.link, '_blank')
          }
        >
          {button.text}
        </RootButton>
      )}
    </Stack>
  )
}

const GetStarted: React.FC = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const errorMessages = useErrorMessages()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [getStarted, setGetStarted] = useSessionStorage<Entry[]>(`getStarted-${language}`, [])
  const [galleryImageByEntry, setGalleryImageByEntry] = useState<GalleryElementsImagesByEntryId>({})

  useEffect(() => {
    if (!getStarted.length) {
      setIsLoading(true)
      client
        .getEntries({ content_type: GET_STARTED_CONTENT_TYPE, locale: language })
        .then((response: any) => {
          const sortedContent = response.items.sort(
            (a: Entry, b: Entry) => a.fields.order - b.fields.order
          )
          setGetStarted(sortedContent)
        })
        .catch((err: any) => {
          setError(err.message)
          setIsLoading(false)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [getStarted, setGetStarted, t, language])

  useEffect(() => {
    if (getStarted) {
      setGalleryImageByEntry(oldGalleryImageByEntry =>
        pullGalleryImage(getStarted, oldGalleryImageByEntry, setGalleryImageByEntry)
      )
    }
  }, [getStarted])

  return (
    <Stack style={{ height: 'inherit', margin: '63px 0 10px 0' }}>
      {isLoading && <Loading />}
      {error && <ErrorMessage message={errorMessages.cmsConnection} details={error} />}
      {!isLoading && !error && (
        <Stack style={{ gap: '10px', alignItems: 'center', width: '100%' }}>
          <Title order={4}>{t(`components.getStarted.title`, 'Get Started on LAMINA1')}</Title>
          <Text>
            {t(
              'components.getStarted.subtitle',
              'Your items, content, and activity will show up here, on your Profile, as you start exploring the LAMINA1 Hub.'
            )}
          </Text>

          <Stack
            style={{
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Carousel
              slideSize={`calc(${100 / getStarted.length}% - 30px)`}
              slideGap="30px"
              previousControlIcon={<IconCaretLeftFilled />}
              nextControlIcon={<IconCaretRightFilled />}
            >
              {getStarted &&
                getStarted.map((elem, index) => {
                  const card = wrapData(elem, galleryImageByEntry)
                  return (
                    <Carousel.Slide key={`${elem}${String(index)}`}>
                      <CTACard
                        image={card.image}
                        title={card.title}
                        button={{
                          text: card.buttonText,
                          link: card.buttonLink,
                        }}
                      />
                    </Carousel.Slide>
                  )
                })}
            </Carousel>
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}

export default GetStarted
